import {Dialog} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'

export type UnstackDialogProps = {
  errorMessage: string
  isConfirmingUnstack: boolean
  isLoading: boolean
  onCancelUnstack: () => void
  onConfirmUnstack: () => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  someLockedPulls?: boolean
}

export function UnstackDialog({
  returnFocusRef,
  onCancelUnstack,
  onConfirmUnstack,
  isConfirmingUnstack,
  isLoading,
  errorMessage,
  someLockedPulls,
}: UnstackDialogProps) {
  if (!isConfirmingUnstack) {
    return null
  }

  const sucessMessage = someLockedPulls
    ? 'Pull requests queued for merge or already merged will remain in the stack. For pull requests that are removed, base branches will not be retargeted and commit histories will remain the same.'
    : 'All pull requests will be removed from the stack. Their base branches will not be retargeted and their commit histories will remain the same.'

  return (
    <Dialog
      title="Are you sure you want to unstack these pull requests?"
      width="large"
      returnFocusRef={returnFocusRef}
      onClose={onCancelUnstack}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onCancelUnstack},
        {
          buttonType: 'danger',
          content: 'Unstack',
          onClick: onConfirmUnstack,
          // aria-disabled must be not set when false to avoid broken styling
          'aria-disabled': isLoading ? 'true' : undefined,
          disabled: isLoading,
          loading: isLoading,
        },
      ]}
    >
      {sucessMessage}
      {errorMessage && (
        <InlineMessage className="mt-2" variant="critical">
          {errorMessage}
        </InlineMessage>
      )}
      {/* Loading message accessible to screen readers only. See https://primer.style/components/button#button-loading-state */}
      <span className="sr-only" aria-live="polite">
        {isLoading ? 'Unstacking pull requests in progress.' : ''}
      </span>
    </Dialog>
  )
}
